import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Card,
  CardContent,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import {
  MortgageType,
  Mortgage,
  MortgageTrack,
  MortgageTrackType,
  PropertyType,
} from "../../interfaces/DTO/mortgage/mortgage";
import {
  formatMortgageType,
  formatMortgageTrackType,
  formatMortgageTrackRepaymentType,
  formatPropertyType,
  UNIFORM_BASKETS,
  MortgageTrackTemplates,
} from "../../utils/mortgageUtil";
import { MortgageTrackRepaymentType } from "../../interfaces/DTO/mortgage/mortgage";
import CurrencyTextField from "../../shared/UIParts/InputFields/CurrencyTextField";
import CalculateIcon from "@mui/icons-material/Calculate";
import SaveIcon from "@mui/icons-material/Save";
import { calculateMortgagePayments, MortgagePayment } from "../../services/mortgageCalculator";
import { MortgageDetails } from "../../interfaces/DTO/mortgage/mortgage";
import MortgageSimulation from "./MortgageSimulation";
import MortgageTracksTable from "./MortgageTracksTable";
import { formatCurrency } from "../../utils/namesFormater";

interface MortgageCalculatorProps {
  onClose?: () => void;
  mortgageDetails?: MortgageDetails; // נתוני משכנתא קיימת
  mode?: 'view' | 'edit'; // מצב תצוגה או עריכה
  onSave?: (updatedMortgageDetails: MortgageDetails) => void | Promise<void>; // callback לשמירת שינויים
  reportedPeriod?: string; // תקופה מדווחת
}

interface TrackWithAmount extends MortgageTrack {
  calculatedAmount: number;
}

const MortgageCalculator: React.FC<MortgageCalculatorProps> = ({
  onClose,
  mortgageDetails,
  mode = 'view',
  onSave,
  reportedPeriod
}) => {
  const isEditMode = mode === 'edit';
  const [isSaving, setIsSaving] = useState(false);

  // סוג משכנתא: חדשה או קיימת
  const [mortgageStatus, setMortgageStatus] = useState<'new' | 'existing'>(
    mortgageDetails ? 'existing' : 'new'
  );

  // אתחול state מתוך mortgageDetails אם קיים
  const [selectedBasketType, setSelectedBasketType] = useState<MortgageType | null>(
    mortgageDetails ? MortgageType.CUSTOM : null
  );
  const [propertyValue, setPropertyValue] = useState<number>(
    mortgageDetails?.property_value || 0
  );
  const [financingPercentage, setFinancingPercentage] = useState<number>(75);
  const [equity, setEquity] = useState<number>(
    mortgageDetails?.equity || 0
  );
  const [propertyType, setPropertyType] = useState<PropertyType | undefined>(
    mortgageDetails?.property_type || PropertyType.READY_MADE
  );
  const [totalAmount, setTotalAmount] = useState<number>(
    mortgageDetails?.total_original_amount || 1000000
  );

  // חישוב defaultPeriodYears מתוך start_date ו-end_date אם קיימים
  const [defaultPeriodYears, setDefaultPeriodYears] = useState<number>(() => {
    if (mortgageDetails?.start_date && mortgageDetails?.end_date) {
      const start = new Date(mortgageDetails.start_date);
      const end = new Date(mortgageDetails.end_date);
      const diffMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
      const years = Math.round(diffMonths / 12);
      return years;
    }
    return 20;
  });

  const [startDate, setStartDate] = useState<string>(
    mortgageDetails?.start_date || new Date().toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState<string | undefined>(
    mortgageDetails?.end_date
  );
  const [isEditingEndDate, setIsEditingEndDate] = useState(false);
  const [customTracks, setCustomTracks] = useState<TrackWithAmount[]>(
    mortgageDetails?.tracks?.map(track => ({
      ...track,
      calculatedAmount: track.amount || 0
    })) || []
  );
  const [selectedRepaymentType, setSelectedRepaymentType] = useState<MortgageTrackRepaymentType>(
    MortgageTrackRepaymentType.SPITZER
  );
  const [showSimulation, setShowSimulation] = useState<boolean>(!!mortgageDetails);
  // עדכוני ריבית מותאמים אישית לכל מסלול (track_type -> interest_rate)
  const [customInterestRates, setCustomInterestRates] = useState<Map<number, number>>(new Map());
  // עדכוני אחוזים מותאמים אישית לכל מסלול (track_index -> percentage)
  const [customPercentages, setCustomPercentages] = useState<Map<number, number>>(new Map());
  // מצב דיאלוג הוספת מסלול
  const [isAddTrackDialogOpen, setIsAddTrackDialogOpen] = useState(false);

  // בדיקה אם יש tracks קיימים במשכנתא
  const hasExistingTracks = mortgageDetails?.tracks && mortgageDetails.tracks.length > 0;

  // האם להציג את רשימת בחירת הסלים
  // true - אם אין mortgageDetails OR אם יש mortgageDetails אבל אין tracks
  const [showBasketSelection, setShowBasketSelection] = useState<boolean>(!hasExistingTracks);

  // קבלת הסל הנבחר
  const selectedBasket = useMemo<Mortgage | null>(() => {
    if (!selectedBasketType) return null;

    if (selectedBasketType === MortgageType.CUSTOM) {
      return {
        basket_type: MortgageType.CUSTOM,
        tracks: customTracks,
      };
    }

    return UNIFORM_BASKETS[selectedBasketType] || null;
  }, [selectedBasketType, customTracks]);

  // חישוב סכומים למסלולים + שילוב עדכוני ריבית ואחוזים מותאמים
  const tracksWithAmounts = useMemo<TrackWithAmount[]>(() => {
    if (!selectedBasket) return [];

    return selectedBasket.tracks.map((track, index) => {
      const customRate = customInterestRates.get(index);
      const customPercentage = customPercentages.get(index);
      const finalPercentage = customPercentage !== undefined ? customPercentage : track.amountPercentage || 0;

      return {
        ...track,
        amountPercentage: finalPercentage,
        calculatedAmount: Math.round(totalAmount * finalPercentage),
        // אם יש ריבית מותאמת אישית, נשתמש בה, אחרת נשתמש בריבית המקורית
        interest_rate: customRate !== undefined ? customRate : track.interest_rate,
      };
    });
  }, [selectedBasket, totalAmount, customInterestRates, customPercentages]);

  // חישוב סך האחוזים ובדיקת תקינות
  const totalPercentage = useMemo(() => {
    return tracksWithAmounts.reduce((sum, track) => sum + (track.amountPercentage || 0), 0) * 100;
  }, [tracksWithAmounts]);

  const isPercentageValid = useMemo(() => {
    return Math.abs(totalPercentage - 100) < 0.01;
  }, [totalPercentage]);

  // פונקציה לחישוב תאריך סיום המשכנתא
  const calculateEndDate = (startDate: string | undefined, loanMonths: number): string | undefined => {
    if (!startDate || loanMonths <= 0) return undefined;

    try {
      const start = new Date(startDate);
      start.setMonth(start.getMonth() + loanMonths);
      return start.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    } catch (error) {
      console.error('Error calculating end date:', error);
      return undefined;
    }
  };

  // חישוב תאריך סיום צפוי
  const calculatedEndDate = useMemo(() => {
    return calculateEndDate(startDate, defaultPeriodYears * 12);
  }, [startDate, defaultPeriodYears]);

  // חישוב מספר שנים מתאריך סיום ידני
  const yearsFromEndDate = useMemo(() => {
    if (!endDate || !startDate) return null;

    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
      return Math.round(diffMonths / 12 * 10) / 10; // עיגול לעשירית
    } catch (error) {
      return null;
    }
  }, [startDate, endDate]);

  // עדכון months של כל המסלולים כאשר יש תאריך סיום ידני
  useEffect(() => {
    if (endDate && yearsFromEndDate !== null && selectedBasketType === MortgageType.CUSTOM) {
      const monthsFromEndDate = Math.round(yearsFromEndDate * 12);
      setCustomTracks((prev) =>
        prev.map((track) => ({ ...track, months: monthsFromEndDate }))
      );
    }
  }, [endDate, yearsFromEndDate, selectedBasketType]);

  // חישוב אוטומטי של סכום משכנתא והון עצמי
  useEffect(() => {
    // אם זו משכנתא קיימת, לא נעדכן אוטומטית
    if (mortgageStatus === 'existing') {
      return;
    }

    // אם יש נתונים שמורים, לא נעדכן אוטומטית
    if (mortgageDetails?.property_value && mortgageDetails?.equity) {
      return;
    }

    if (propertyValue > 0) {
      // חישוב סכום משכנתא מאחוז המימון
      const calculatedMortgageAmount = Math.round(propertyValue * (financingPercentage / 100));
      setTotalAmount(calculatedMortgageAmount);

      // חישוב הון עצמי
      const calculatedEquity = propertyValue - calculatedMortgageAmount;
      setEquity(calculatedEquity);
    }
  }, [propertyValue, financingPercentage, mortgageDetails, mortgageStatus]);

  // עדכון ריבית של מסלול ספציפי
  const handleUpdateInterestRate = (trackIndex: number, newRate: number) => {
    if (selectedBasketType === MortgageType.CUSTOM) {
      setCustomTracks((prev) =>
        prev.map((track, idx) =>
          idx === trackIndex ? { ...track, interest_rate: newRate } : track
        )
      );
    } else {
      // עבור סלים אחידים, נשמור את השינויים במפה נפרדת
      setCustomInterestRates((prev) => {
        const updated = new Map(prev);
        updated.set(trackIndex, newRate);
        return updated;
      });
    }
  };

  // עדכון מרווח ריבית של מסלול ספציפי
  const handleUpdateInterestMargin = (trackIndex: number, newMargin: number) => {
    if (selectedBasketType === MortgageType.CUSTOM) {
      setCustomTracks((prev) =>
        prev.map((track, idx) =>
          idx === trackIndex ? { ...track, interest_margin: newMargin } : track
        )
      );
    } else {
      // TODO: עבור סלים אחידים, נצטרך להוסיף מפה נפרדת למרווחי ריבית
      // כרגע נעדכן ישירות בתוך customTracks
      setCustomTracks((prev) =>
        prev.map((track, idx) =>
          idx === trackIndex ? { ...track, interest_margin: newMargin } : track
        )
      );
    }
  };

  // עדכון months של מסלול ספציפי
  const handleUpdateTrackMonths = (trackIndex: number, newMonths: number) => {
    if (selectedBasketType === MortgageType.CUSTOM) {
      setCustomTracks((prev) =>
        prev.map((track, idx) =>
          idx === trackIndex ? { ...track, months: newMonths } : track
        )
      );
    } else {
      // עבור סלים אחידים, נעבור למצב CUSTOM עם כל המסלולים הקיימים + עדכון ה-months
      setSelectedBasketType(MortgageType.CUSTOM);
      setCustomTracks(
        tracksWithAmounts.map((track, idx) =>
          idx === trackIndex ? { ...track, months: newMonths } : track
        )
      );
      // איפוס המפות של עדכונים מותאמים
      setCustomInterestRates(new Map());
      setCustomPercentages(new Map());
    }
  };

  // מחיקת מסלול
  const handleDeleteTrack = (trackIndex: number) => {
    if (selectedBasketType === MortgageType.CUSTOM) {
      setCustomTracks((prev) => prev.filter((_, idx) => idx !== trackIndex));

      // עדכון המפות של ריביות ואחוזים מותאמים
      setCustomInterestRates((prev) => {
        const updated = new Map(prev);
        updated.delete(trackIndex);
        // עדכון האינדקסים של המסלולים שאחריו
        const newMap = new Map();
        Array.from(updated.entries()).forEach(([key, value]) => {
          if (key > trackIndex) {
            newMap.set(key - 1, value);
          } else {
            newMap.set(key, value);
          }
        });
        return newMap;
      });

      setCustomPercentages((prev) => {
        const updated = new Map(prev);
        updated.delete(trackIndex);
        // עדכון האינדקסים של המסלולים שאחריו
        const newMap = new Map();
        Array.from(updated.entries()).forEach(([key, value]) => {
          if (key > trackIndex) {
            newMap.set(key - 1, value);
          } else {
            newMap.set(key, value);
          }
        });
        return newMap;
      });
    }
  };

  // בחירת סל מוכן
  const handleSelectBasket = (basketType: MortgageType) => {
    setSelectedBasketType(basketType);
    // איפוס עדכוני ריבית ואחוזים מותאמים אישית כשעוברים לסל חדש
    setCustomInterestRates(new Map());
    setCustomPercentages(new Map());

    // אם בחרנו CUSTOM, נאתחל עם מסלול ברירת מחדל אחד
    if (basketType === MortgageType.CUSTOM) {
      setCustomTracks([
        {
          ...MortgageTrackTemplates.FIXED_UNLINKED,
          amountPercentage: 1.0,
          calculatedAmount: totalAmount,
          months: defaultPeriodYears * 12,
        },
      ]);
    }
  };

  // הוספת מסלול למותאם אישית
  const handleAddCustomTrack = (trackType: MortgageTrackType) => {
    const newTrack: TrackWithAmount = {
      ...MortgageTrackTemplates[trackType],
      amountPercentage: 0,
      calculatedAmount: 0,
      months: defaultPeriodYears * 12, // אתחול months לפי תקופת ברירת המחדל
    };

    // אם זה סל CUSTOM, פשוט נוסיף את המסלול
    if (selectedBasketType === MortgageType.CUSTOM) {
      setCustomTracks((prev) => [...prev, newTrack]);
    } else {
      // אם זה סל אחיד, נעבור למצב CUSTOM עם כל המסלולים הקיימים + החדש
      setSelectedBasketType(MortgageType.CUSTOM);
      setCustomTracks([...tracksWithAmounts, newTrack]);
      // איפוס המפות של עדכונים מותאמים
      setCustomInterestRates(new Map());
      setCustomPercentages(new Map());
    }

    setIsAddTrackDialogOpen(false); // סגירת הדיאלוג לאחר הוספת המסלול
  };

  // עדכון אחוז מסלול - עובד לכל סוגי הסלים
  const handleUpdateTrackPercentage = (trackIndex: number, newPercentage: number) => {
    if (selectedBasketType === MortgageType.CUSTOM) {
      // עבור CUSTOM - עדכון במצב customTracks
      setCustomTracks((prev) =>
        prev.map((track, idx) =>
          idx === trackIndex
            ? {
                ...track,
                amountPercentage: newPercentage / 100,
                calculatedAmount: Math.round(totalAmount * (newPercentage / 100)),
              }
            : track
        )
      );
    } else {
      // עבור סלים אחידים - שמירה במפה נפרדת
      setCustomPercentages((prev) => {
        const updated = new Map(prev);
        updated.set(trackIndex, newPercentage / 100);
        return updated;
      });
    }
  };

  // מחיקת מסלול במותאם אישית
  const handleRemoveCustomTrack = (trackIndex: number) => {
    setCustomTracks((prev) => prev.filter((_, idx) => idx !== trackIndex));
  };

  // חישוב תשלומים לכל מסלול
  const trackPayments = useMemo<Map<number, MortgagePayment[]>>(() => {
    if (tracksWithAmounts.length === 0) {
      return new Map();
    }

    const paymentsMap = new Map<number, MortgagePayment[]>();
    tracksWithAmounts.forEach((track, index) => {
      const payments = calculateMortgagePayments({
        amount: track.calculatedAmount,
        months: track.months || defaultPeriodYears * 12,
        annualInterestRate: track.interest_rate || 0,
        repaymentType: selectedRepaymentType,
      });
      paymentsMap.set(index, payments);
    });

    return paymentsMap;
  }, [tracksWithAmounts, selectedRepaymentType, defaultPeriodYears]);

  // פונקציה לשמירת נתוני משכנתא
  const handleSave = async () => {
    if (!onSave) return;

    setIsSaving(true);
    try {
      // שימוש בתאריך הסיום הידני אם קיים, אחרת חישוב אוטומטי
      const finalEndDate = endDate || calculateEndDate(
        startDate,
        defaultPeriodYears * 12
      );

      const updatedMortgageDetails: MortgageDetails = {
        ...mortgageDetails,
        start_date: startDate,
        total_original_amount: totalAmount,
        total_current_balance: tracksWithAmounts.reduce((sum, track) => sum + track.calculatedAmount, 0),
        property_value: propertyValue > 0 ? propertyValue : undefined,
        equity: equity > 0 ? equity : undefined,
        property_type: propertyType,
        end_date: finalEndDate,
        tracks: tracksWithAmounts.map(track => ({
          track_type: track.track_type,
          amount: track.calculatedAmount,
          amountPercentage: track.amountPercentage,
          interest_rate: track.interest_rate,
          repayment_type: track.repayment_type,
          indexation_type: track.indexation_type,
          interest_base_type: track.interest_base_type,
          interest_margin: track.interest_margin,
          months: track.months,
          interest_update_interval_years: track.interest_update_interval_years,
        })),
        monthly_payment_total: tracksWithAmounts.reduce((sum, track) => {
          const trackIndex = tracksWithAmounts.indexOf(track);
          const payments = trackPayments.get(trackIndex);
          return sum + (payments?.[0]?.totalPayment || 0);
        }, 0),
      };

      await onSave(updatedMortgageDetails);
    } catch (error) {
      console.error('Failed to save mortgage details:', error);
      alert('שגיאה בשמירת פרטי המשכנתא');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4, textAlign: "center", fontWeight: "bold" }}>
        מחשבון משכנתא
      </Typography>

      {/* בחירת סוג משכנתא */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ mb: 2, fontWeight: "bold" }}>
            סוג המשכנתא
          </FormLabel>
          <RadioGroup
            row
            value={mortgageStatus}
            onChange={(e) => setMortgageStatus(e.target.value as 'new' | 'existing')}
          >
            <FormControlLabel
              value="new"
              control={<Radio />}
              label="משכנתא חדשה"
            />
            <FormControlLabel
              value="existing"
              control={<Radio />}
              label="משכנתא קיימת"
            />
          </RadioGroup>
        </FormControl>
      </Paper>

      {/* פרטי הנכס - רק למשכנתא חדשה */}
      {mortgageStatus === 'new' && (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
          פרטי הנכס
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* שווי הנכס */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body1" sx={{ minWidth: "150px" }}>
              שווי הנכס:
            </Typography>
            <CurrencyTextField
              value={propertyValue}
              onChange={setPropertyValue}
              sx={{ maxWidth: "250px" }}
            />
          </Box>

          {/* בחירה מהירה של שווי נכס */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: "150px", ml: 1 }}>
              בחירה מהירה:
            </Typography>
            {[2000000, 2750000, 3500000].map((amount) => (
              <Chip
                key={amount}
                label={`${(amount / 1000000).toFixed(2)}M ₪`}
                onClick={() => setPropertyValue(amount)}
                color={propertyValue === amount ? "primary" : "default"}
                variant={propertyValue === amount ? "filled" : "outlined"}
                sx={{ fontSize: "0.875rem" }}
              />
            ))}
          </Box>

          {/* סוג נכס */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body1" sx={{ minWidth: "150px" }}>
              סוג נכס:
            </Typography>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>בחר סוג נכס</InputLabel>
              <Select
                value={propertyType || ""}
                onChange={(e) => setPropertyType(e.target.value as PropertyType)}
                label="בחר סוג נכס"
              >
                <MenuItem value={PropertyType.UNDER_CONSTRUCTION}>
                  {formatPropertyType(PropertyType.UNDER_CONSTRUCTION)}
                </MenuItem>
                <MenuItem value={PropertyType.FROM_CONTRACTOR}>
                  {formatPropertyType(PropertyType.FROM_CONTRACTOR)}
                </MenuItem>
                <MenuItem value={PropertyType.READY_MADE}>
                  {formatPropertyType(PropertyType.READY_MADE)}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* אחוז מימון */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body1" sx={{ minWidth: "150px" }}>
              אחוז מימון:
            </Typography>
            <TextField
              type="number"
              value={financingPercentage}
              onChange={(e) => setFinancingPercentage(Number(e.target.value))}
              sx={{ maxWidth: "150px" }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{
                min: 0,
                max: 100,
                step: 1,
              }}
            />
          </Box>

          {/* הון עצמי */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body1" sx={{ minWidth: "150px" }}>
              הון עצמי:
            </Typography>
            <CurrencyTextField
              value={equity}
              onChange={setEquity}
              sx={{ maxWidth: "250px" }}
            />
          </Box>
        </Box>
        </Paper>
      )}

      {/* שדה סכום כולל */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h6" sx={{ minWidth: "150px" }}>
              {mortgageStatus === 'existing' ? 'סכום המשכנתא המקורי:' : 'סכום המשכנתא:'}
            </Typography>
            <CurrencyTextField
              value={totalAmount}
              onChange={setTotalAmount}
              sx={{ maxWidth: "250px" }}
            />
          </Box>

          {/* בחירה מהירה של סכומים */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center", ml: 1 }}>
              בחירה מהירה:
            </Typography>
            {[1000000, 1500000, 2000000, 2500000].map((amount) => (
              <Chip
                key={amount}
                label={`${(amount / 1000000).toFixed(1)}M ₪`}
                onClick={() => setTotalAmount(amount)}
                color={totalAmount === amount ? "primary" : "default"}
                variant={totalAmount === amount ? "filled" : "outlined"}
                sx={{ fontSize: "0.875rem" }}
              />
            ))}
          </Box>

          {/* תאריך התחלת משכנתא */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: "150px" }}>
              תאריך התחלה:
            </Typography>
            <TextField
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              sx={{ maxWidth: "200px" }}
              size="small"
            />
          </Box>

          {/* בחירת תקופת משכנתא */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              תקופת משכנתא (שנים):
            </Typography>
            {(() => {
              const standardYears = [5, 10, 15, 20, 25, 30, 35];

              // בניית רשימת כל השנים כולל מותאם אישית
              let allYearsData: Array<{ value: number; displayValue: string; isCustom: boolean }> =
                standardYears.map(y => ({ value: y, displayValue: y.toString(), isCustom: false }));

              // אם יש תאריך סיום ידני, בדיקה אם צריך להוסיף Chip מותאם אישית
              if (yearsFromEndDate !== null) {
                const roundedYear = Math.round(yearsFromEndDate);
                const hasDecimal = Math.abs(yearsFromEndDate - roundedYear) > 0.05; // אם יש הבדל משמעותי

                if (!standardYears.includes(roundedYear)) {
                  // אם מספר השנים המעוגל לא קיים ב-Chips, נוסיף אותו
                  allYearsData.push({
                    value: yearsFromEndDate,
                    displayValue: hasDecimal ? yearsFromEndDate.toFixed(1) : roundedYear.toString(),
                    isCustom: true
                  });
                } else if (hasDecimal) {
                  // אם מספר השנים המעוגל כן קיים אבל יש עשיריות, נוסיף Chip נפרד
                  allYearsData.push({
                    value: yearsFromEndDate,
                    displayValue: yearsFromEndDate.toFixed(1),
                    isCustom: true
                  });
                }

                // מיון לפי ערך מספרי
                allYearsData.sort((a, b) => a.value - b.value);
              }

              return allYearsData.map((yearData, index) => {
                const isSelected = yearData.isCustom
                  ? endDate !== undefined
                  : !endDate && defaultPeriodYears === yearData.value;

                return (
                  <Chip
                    key={yearData.isCustom ? 'custom' : yearData.value}
                    label={yearData.displayValue}
                    onClick={() => {
                      if (!yearData.isCustom) {
                        setDefaultPeriodYears(yearData.value);
                        setEndDate(undefined); // איפוס תאריך סיום ידני
                        // דריסת כל ה-months של כל המסלולים
                        if (selectedBasketType === MortgageType.CUSTOM) {
                          setCustomTracks((prev) =>
                            prev.map((track) => ({ ...track, months: yearData.value * 12 }))
                          );
                        }
                      }
                    }}
                    color={isSelected ? "primary" : "default"}
                    variant={isSelected ? "filled" : "outlined"}
                    size="small"
                    sx={{ fontSize: "0.875rem" }}
                  />
                );
              });
            })()}
          </Box>

          {/* תאריך סיום משוער או עריכה ידנית */}
          {isEditingEndDate ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: "150px" }}>
                תאריך סיום:
              </Typography>
              <TextField
                type="date"
                value={endDate || calculatedEndDate || ''}
                onChange={(e) => setEndDate(e.target.value)}
                sx={{ maxWidth: "200px" }}
                size="small"
              />
            </Box>
          ) : (
            calculatedEndDate && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: "150px" }}>
                  תאריך סיום משוער:
                </Typography>
                <Typography variant="body2" color="primary" sx={{ fontWeight: "bold" }}>
                  {new Date(endDate || calculatedEndDate).toLocaleDateString('he-IL', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setIsEditingEndDate(true)}
                  sx={{
                    width: 24,
                    height: 24,
                    color: "text.secondary",
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )
          )}
        </Box>
      </Paper>
   {/* כפתורי בחירת סוג החזר לסימולציה */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
              בחר סוג החזר :
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant={selectedRepaymentType === MortgageTrackRepaymentType.SPITZER ? "contained" : "outlined"}
                size="large"
                onClick={() => {
                  setSelectedRepaymentType(MortgageTrackRepaymentType.SPITZER);
                  setShowSimulation(true);
                }}
                disabled={!isPercentageValid}
                fullWidth
                sx={{
                  py: 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  textAlign: "right",
                  textTransform: "none",
                  flex: 1,
                  border: selectedRepaymentType === MortgageTrackRepaymentType.SPITZER ? "2px solid" : "1px solid",
                  borderColor: selectedRepaymentType === MortgageTrackRepaymentType.SPITZER ? "primary.main" : "divider",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <CalculateIcon />
                  <Typography variant="h6" fontWeight="bold">
                    {formatMortgageTrackRepaymentType(MortgageTrackRepaymentType.SPITZER)}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ fontSize: "0.75rem", opacity: 0.9, lineHeight: 1.4 }}>
                  תשלום קבוע הכולל גם ריבית וגם חלק מהקרן.
                  עם הזמן, חלק הריבית בתשלום קטן, וחלק הקרן גדל
                </Typography>
              </Button>

              <Button
                variant={selectedRepaymentType === MortgageTrackRepaymentType.FIXED_PRINCIPAL ? "contained" : "outlined"}
                size="large"
                onClick={() => {
                  setSelectedRepaymentType(MortgageTrackRepaymentType.FIXED_PRINCIPAL);
                  setShowSimulation(true);
                }}
                disabled={!isPercentageValid}
                fullWidth
                color="secondary"
                sx={{
                  py: 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  textAlign: "right",
                  textTransform: "none",
                  flex: 1,
                  border: selectedRepaymentType === MortgageTrackRepaymentType.FIXED_PRINCIPAL ? "2px solid" : "1px solid",
                  borderColor: selectedRepaymentType === MortgageTrackRepaymentType.FIXED_PRINCIPAL ? "secondary.main" : "divider",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <CalculateIcon />
                  <Typography variant="h6" fontWeight="bold">
                    {formatMortgageTrackRepaymentType(MortgageTrackRepaymentType.FIXED_PRINCIPAL)}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ fontSize: "0.75rem", opacity: 0.9, lineHeight: 1.4 }}>
                  קרן קבועה בכל חודש, הריבית יורדת עם הזמן כי היא מחושבת על יתרת קרן הולכת ופוחתת
                </Typography>
              </Button>

              <Button
                variant={selectedRepaymentType === MortgageTrackRepaymentType.BULLET ? "contained" : "outlined"}
                size="large"
                onClick={() => {
                  setSelectedRepaymentType(MortgageTrackRepaymentType.BULLET);
                  setShowSimulation(true);
                }}
                disabled={!isPercentageValid}
                fullWidth
                color="info"
                sx={{
                  py: 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  textAlign: "right",
                  textTransform: "none",
                  flex: 1,
                  border: selectedRepaymentType === MortgageTrackRepaymentType.BULLET ? "2px solid" : "1px solid",
                  borderColor: selectedRepaymentType === MortgageTrackRepaymentType.BULLET ? "info.main" : "divider",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <CalculateIcon />
                  <Typography variant="h6" fontWeight="bold">
                    {formatMortgageTrackRepaymentType(MortgageTrackRepaymentType.BULLET)}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ fontSize: "0.75rem", opacity: 0.9, lineHeight: 1.4 }}>
                  משלמים רק את הריבית לאורך תקופת ההלוואה,
                  והקרן כולה נפרעת בסוף התקופה (בתשלום אחד)
                </Typography>
              </Button>
            </Box>

           
          </Box>

      {/* בחירת סל - או קובייה מסכמת למשכנתא עם tracks קיימים */}
      {!hasExistingTracks || showBasketSelection ? (
        // תצוגת בחירת סלים (למשכנתא חדשה, ללא tracks, או כשלוחצים "שנה סל")
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 3, textAlign: "center" }}>
            בחר סל משכנתא:
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 3,
              overflowX: "auto",
              pb: 2,
              px: 1,
            }}
          >
            {Object.values(MortgageType).map((type) => {
              const isSelected = selectedBasketType === type;
              const basket = type === MortgageType.CUSTOM ? null : UNIFORM_BASKETS[type];

              return (
                <Card
                  key={type}
                  onClick={() => {
                    handleSelectBasket(type);
                    if (hasExistingTracks) {
                      // אם יש tracks קיימים, נסגור את בחירת הסלים אחרי הבחירה
                      setShowBasketSelection(false);
                    }
                  }}
                  sx={{
                    minWidth: 280,
                    maxWidth: 320,
                    cursor: "pointer",
                    border: isSelected ? "3px solid" : "1px solid",
                    borderColor: isSelected ? "primary.main" : "divider",
                    bgcolor: isSelected ? "primary.50" : "background.paper",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "primary.main",
                      transform: "translateY(-4px)",
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {/* כותרת */}
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color={isSelected ? "primary.main" : "text.primary"}
                          gutterBottom
                        >
                          {formatMortgageType(type)}
                        </Typography>
                        {type === MortgageType.CUSTOM && (
                          <Typography variant="caption" color="text.secondary">
                            הרכב את הסל שלך בעצמך
                          </Typography>
                        )}
                      </Box>

                      {/* תצוגת מסלולים בסל */}
                      {basket && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          {basket.tracks.map((track, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                py: 0.5,
                                px: 1,
                                bgcolor: "background.default",
                                borderRadius: 1,
                              }}
                            >
                              <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                                {formatMortgageTrackType(track.track_type)}
                              </Typography>
                              <Typography variant="caption" fontWeight="bold">
                                {((track.amountPercentage || 0) * 100).toFixed(0)}%
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      ) : (
        // קובייה מסכמת למשכנתא קיימת
        <Box sx={{ mb: 4 }}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              border: "2px solid",
              borderColor: "primary.main",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight="bold" color="primary.main" gutterBottom>
                  נתוני מסלולים
                </Typography>
                <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {tracksWithAmounts.map((track, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 1.5,
                        bgcolor: "background.paper",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                        <Typography variant="body1" fontWeight="medium">
                          {formatMortgageTrackType(track.track_type)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {((track.amountPercentage || 0) * 100).toFixed(0)}%
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Typography variant="body2" fontWeight="bold">
                          ₪{track.calculatedAmount.toLocaleString()}
                        </Typography>
                        {track.interest_rate && (
                          <Typography variant="body2" color="text.secondary">
                            ריבית: {track.interest_rate.toFixed(2)}%
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* סיכום החזר חודשי צפוי כולל */}
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 1,
                    border: "2px solid",
                    borderColor: "secondary.main",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6" fontWeight="bold" color="secondary.dark">
                      החזר חודשי צפוי (כולל)
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="secondary.dark">
                      {formatCurrency(
                        tracksWithAmounts.reduce((total, track) => {
                          const payments = calculateMortgagePayments({
                            amount: track.calculatedAmount,
                            months: defaultPeriodYears * 12,
                            annualInterestRate: track.interest_rate || 0,
                            repaymentType: selectedRepaymentType,
                          });
                          return total + (payments.length > 0 ? payments[0].totalPayment : 0);
                        }, 0)
                      )}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    בהתאם לסוג החזר: {formatMortgageTrackRepaymentType(selectedRepaymentType)}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setShowBasketSelection(true)}
                sx={{
                  borderRadius: "50%",
                  minWidth: 56,
                  width: 56,
                  height: 56,
                  p: 0,
                }}
              >
                <Typography variant="caption" sx={{ fontSize: "0.65rem", textAlign: "center", lineHeight: 1.2 }}>
                  שנה<br />סל
                </Typography>
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    
      {/* מסר אם לא נבחר סל */}
      {!selectedBasket && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            בחר סל משכנתא כדי להתחיל
          </Typography>
        </Box>
      )}
      {/* תצוגת מסלולים */}
      {selectedBasket && tracksWithAmounts.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            מסלולי המשכנתא בסל {formatMortgageType(selectedBasketType!)}:
          </Typography>

          {/* טבלת מסלולים */}
          <MortgageTracksTable
            tracks={tracksWithAmounts}
            selectedBasketType={selectedBasketType!}
            defaultPeriodYears={defaultPeriodYears}
            selectedRepaymentType={selectedRepaymentType}
            trackPayments={trackPayments}
            startDate={startDate}
            onUpdatePercentage={handleUpdateTrackPercentage}
            onUpdateInterestRate={handleUpdateInterestRate}
            onUpdateInterestMargin={handleUpdateInterestMargin}
            onDeleteTrack={selectedBasketType === MortgageType.CUSTOM ? handleDeleteTrack : undefined}
            onUpdatePeriodYears={isEditMode ? setDefaultPeriodYears : undefined}
            onUpdateTrackMonths={isEditMode ? handleUpdateTrackMonths : undefined}
            isEditable={true}
          />

          {/* הוספת מסלול - זמין לכל סוגי הסלים במצב עריכה */}
          {isEditMode && selectedBasket && (
            <Box
              sx={{
                mt: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Fab
                color="secondary"
                aria-label="add-track"
                onClick={() => setIsAddTrackDialogOpen(true)}
                sx={{
                  width: 70,
                  height: 70,
                }}
              >
                <AddIcon sx={{ fontSize: 32 }} />
              </Fab>
              <Typography variant="body2" sx={{ mt: 1, fontWeight: "bold" }}>
                הוסף מסלול
              </Typography>
            </Box>
          )}

          {/* דיאלוג בחירת מסלול */}
          <Dialog
            open={isAddTrackDialogOpen}
            onClose={() => setIsAddTrackDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
              בחר סוג מסלול
            </DialogTitle>
            <DialogContent>
              <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
                {Object.values(MortgageTrackType).map((trackType) => (
                  <Button
                    key={trackType}
                    variant="outlined"
                    onClick={() => handleAddCustomTrack(trackType)}
                    sx={{
                      py: 2,
                      textAlign: "center",
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: "medium",
                      color: "text.primary",
                      borderColor: "divider",
                      "&:hover": {
                        borderColor: "primary.main",
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    {formatMortgageTrackType(trackType)}
                  </Button>
                ))}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsAddTrackDialogOpen(false)} color="inherit">
                ביטול
              </Button>
            </DialogActions>
          </Dialog>

          {/* אזהרת תקינות אחוזים */}
          {!isPercentageValid && (
            <Paper sx={{ p: 2, mt: 3, bgcolor: "#ffebee", border: "1px solid #ef5350" }}>
              <Typography variant="body1" color="error" fontWeight="bold">
                ⚠️ סכום האחוזים אינו תקין
              </Typography>
              <Typography variant="body2" color="error">
                סה"כ אחוז מכוסה: {totalPercentage.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="error">
                יש להגיע ל-100% בדיוק כדי לכסות את כל סכום המשכנתא
              </Typography>
            </Paper>
          )}

          {/* אזהרה על ריבית קבועה - לפי חוקי בנק ישראל */}
          {(() => {
            // בדיקה האם יש מסלול קבוע לא צמוד
            const hasFixedUnlinked = tracksWithAmounts.some(
              track => track.track_type === MortgageTrackType.FIXED_UNLINKED
            );

            // חישוב אחוז הריבית הקבועה (רק לא צמודה)
            const fixedPercentage = tracksWithAmounts.reduce((sum, track) => {
              const isFixed = track.track_type === MortgageTrackType.FIXED_UNLINKED;
              return sum + (isFixed ? (track.amountPercentage || 0) * 100 : 0);
            }, 0);

            const minFixedRequired = 100 * (1 / 3); // 33.33%

            // הצג התראה אם:
            // 1. אין בכלל מסלול קבוע לא צמוד, או
            // 2. יש מסלול אבל האחוז שלו נמוך מ-33.33%
            if (!hasFixedUnlinked || fixedPercentage < minFixedRequired) {
              return (
                <Paper sx={{ p: 2, mt: 3, bgcolor: "#fff3e0", border: "1px solid #ff9800" }}>
                  <Typography variant="body1" color="warning.dark" fontWeight="bold">
                    ⚠️ אחוז ריבית קבועה נמוך מהמינימום הנדרש
                  </Typography>
                  <Typography variant="body2" color="warning.dark">
                    אחוז ריבית קבועה לא צמודה: {fixedPercentage.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="warning.dark">
                    לפי חוקי בנק ישראל, לפחות {minFixedRequired.toFixed(1)}% (שליש) מהמשכנתא צריכה להיות בריבית קבועה לא צמודה
                  </Typography>
                </Paper>
              );
            }
            return null;
          })()}

            {/* כפתור שמירה - רק במצב עריכה */}
            {isEditMode && onSave && (
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSave}
                  disabled={isSaving || tracksWithAmounts.length === 0 || !isPercentageValid}
                  startIcon={<SaveIcon sx={{ ml: 1 }} />}
                  sx={{ minWidth: 200 }}
                >
                  {isSaving ? "שומר..." : "שמור פרטי משכנתא"}
                </Button>
              </Box>
            )}

          {/* תצוגת סימולציה עם טאבים */}
          {trackPayments.size > 0 && isPercentageValid && (
            <MortgageSimulation
              tracksWithAmounts={tracksWithAmounts}
              trackPayments={trackPayments}
              selectedRepaymentType={selectedRepaymentType}
              defaultPeriodYears={defaultPeriodYears}
              startDate={startDate}
              onPeriodChange={setDefaultPeriodYears}
            />
          )}

        
        </Box>
      )}

    
    </Box>
  );
};

export default MortgageCalculator;
